/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Config
import physiomKineApi from '@/configs/physiom-kine-api.config';

// Helpers
import { makeUrl } from '@/helpers/api.helper';

// Types
import { User } from '@/types/user';

/* -------------------------------------------------------------------------- */
/*                               Service class                                */
/* -------------------------------------------------------------------------- */
type SignupArgs = {
  password: string;
  invitationKey: string;
};

type LoginArgs = {
  email: string;
  password: string;
};

type ForgotPasswordArgs = {
  email: string;
};

type ResetPasswordArgs = {
  uid: string;
  token: string;
  newPassword: string;
};

type UpdateMeArgs = {
  id: string;
  rpps?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
};

class AuthService {
  csrf = async () => {
    const apiResponse = await physiomKineApi.get(makeUrl({ path: '/csrf/' }));
    return apiResponse;
  };

  signup = async (args: SignupArgs) => {
    const apiResponse = await physiomKineApi.post(
      makeUrl({ path: `/auth/signup` }),
      {
        password: args.password,
        invitation_key: args.invitationKey,
      }
    );

    return apiResponse;
  };

  login = async (args: LoginArgs): Promise<User> => {
    const apiResponse = (await physiomKineApi.post(
      makeUrl({ path: `/auth/signin` }),
      args
    )) as User;

    return apiResponse;
  };

  logout = async () => {
    const apiResponse = await physiomKineApi.delete(
      makeUrl({ path: `/auth/signout` })
    );

    return apiResponse;
  };

  me = async (): Promise<User> => {
    const apiResponse = (await physiomKineApi.get(
      makeUrl({ path: `/users/me` })
    )) as User;

    return apiResponse;
  };

  forgotPassword = async (args: ForgotPasswordArgs) => {
    const apiResponse = await physiomKineApi.post(
      makeUrl({ path: '/auth/password-reset/' }),
      {
        ...args,
      }
    );

    return apiResponse;
  };

  resetPassword = async (args: ResetPasswordArgs) => {
    const apiResponse = await physiomKineApi.post(
      makeUrl({ path: '/auth/password-reset-confirm/' }),
      {
        ...args,
        new_password: args.newPassword,
      }
    );

    return apiResponse;
  };

  updateMe = async (args: UpdateMeArgs) => {
    const apiResponse = (await physiomKineApi.patch(
      makeUrl({ path: `/users/${args.id}/` }),
      {
        ...args,
      }
    )) as User;

    return apiResponse;
  };
}

export { AuthService };
